import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DropdownComponent } from "@bssh/comp-lib";
import { CustomColDef } from "../custom-colDef";

@Component({
  selector: "bssh-column-switcher",
  templateUrl: "./bssh-column-switcher.component.html",
  styleUrls: ["./bssh-column-switcher.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BsshColumnSwitcherComponent implements OnInit {
  @Input() public title: string = "";
  @Input() public selectAllTitle: string = "";
  @Input() public columnDefs: CustomColDef[] = [];
  // tslint:disable-next-line: no-output-on-prefix
  @Output() public onChange: EventEmitter<{ colId: string; state: boolean }[]> =
    new EventEmitter();

  @ViewChild(DropdownComponent, { static: true })
  columnSwitcherDropdown: DropdownComponent;

  public get isAllSelected(): boolean {
    return this.isAllChecked();
  }

  constructor() {}

  ngOnInit() {
    this.setHideValues();
  }

  /**
   * True when the column is marked as always-visible (required).
   * Required columns render as disabled + checked and cannot be hidden
   * via the switcher or the Select All toggle.
   */
  public isRequired(column: CustomColDef): boolean {
    return !!column && column.showAlways === true;
  }

  public dropdownToggle(dropdown: DropdownComponent) {
    if (dropdown.showing) {
      dropdown.hide();
    } else {
      dropdown.show();
    }
  }

  /**
   * Handles the "Select All" checkbox. Required columns are skipped so
   * they always remain visible regardless of the toggle state.
   */
  public setHideValues(event?: Event | any) {
    let checked;
    if (event !== undefined) {
      checked = event.target.checked;
    }
    this.columnDefs.forEach(
      (column: CustomColDef, index: number, columns: CustomColDef[]) => {
        if (this.isRequired(column)) {
          columns[index].hide = false;
          return;
        }
        if (column.hide === undefined && checked === undefined) {
          columns[index].hide = false;
        } else if (typeof checked === "boolean") {
          columns[index].hide = !checked;
        }
      }
    );
    this.updateColumns(
      this.columnDefs
        .filter((c) => !this.isRequired(c))
        .map((c) => ({
          colId: c.colId ? c.colId : c.field,
          state: !c.hide,
        }))
    );
  }

  /**
   * Handles a per-row checkbox toggle. Required columns short-circuit and
   * re-assert their checked state so stray clicks don't leave a visual mismatch.
   */
  public setColumnsVisibility(
    field: string,
    event: Event
  ) {
    const col: CustomColDef = this.columnDefs.find(
      (column: CustomColDef) =>
        column.field === field || column.colId === field
    );
    if (!col) {
      return;
    }
    if (this.isRequired(col)) {
      col.hide = false;
      if (event && (event.target as HTMLInputElement)) {
        (event.target as HTMLInputElement).checked = true;
      }
      return;
    }
    const state: boolean = (event.target as any).checked;
    col.hide = !state;
    this.updateColumns([
      { colId: col.colId ? col.colId : col.field, state },
    ]);
  }

  public updateColumns(update: { colId: string; state: boolean }[]) {
    this.onChange.emit(update);
  }

  /**
   * Whether every non-required column is visible. Required columns are
   * excluded from this calculation so they don't artificially force
   * Select All to be true when other columns are hidden.
   */
  private isAllChecked(): boolean {
    const togglable = this.columnDefs.filter((c) => !this.isRequired(c));
    if (togglable.length === 0) {
      return true;
    }
    return togglable.every((column) => column.hide === false);
  }

}
